import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { showSuccess, showError } from '../utils/toast';
import {
  FaUsers,
  FaWallet,
  FaUserPlus,
  FaChartLine,
  FaSyncAlt,
  FaCircle,
  FaBullhorn,
  FaEnvelope,
  FaBalanceScale
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { darkMode: isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupReport, setCleanupReport] = useState(null);

  useEffect(() => {
    // Auth yüklenene kadar bekle
    if (authLoading) return;

    // Admin kontrolü
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }

    fetchDashboardStats();

    // 10 saniyede bir otomatik yenileme
    const interval = setInterval(() => {
      fetchDashboardStats(true); // silent refresh
    }, 10000);

    return () => clearInterval(interval);
  }, [user, authLoading, navigate]);

  const fetchDashboardStats = async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const { data } = await axios.get('/api/admin/dashboard');
      setStats(data);
    } catch (error) {
      console.error('Stats yüklenemedi:', error);
      if (!silent) {
        showError('İstatistikler yüklenirken hata oluştu!');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardStats();
    setRefreshing(false);
    showSuccess('Veriler yenilendi! 🔄');
  };

  const handleDuplicateCleanup = async (execute = false) => {
    setCleanupLoading(true);
    try {
      const query = execute ? 'execute=true' : 'dryRun=true';
      const { data } = await axios.post(`/api/admin/cleanup-duplicate-assets?${query}`);
      setCleanupReport(data);
      showSuccess(execute
        ? `${data.deactivateCount} legacy kayıt pasifleştirildi.`
        : `${data.duplicateRecordCount} duplicate kayıt bulundu.`);
    } catch (error) {
      showError(error.response?.data?.message || 'Duplicate cleanup çalıştırılamadı.');
    } finally {
      setCleanupLoading(false);
    }
  };

  const getAssetTypeName = (type) => {
    const types = {
      'dolar': 'Dolar',
      'euro': 'Euro',
      'sterlin': 'Sterlin',
      'gram-altin': 'Gram Altın',
      'ceyrek-altin': 'Çeyrek Altın',
      'yarim-altin': 'Yarım Altın',
      'tam-altin': 'Tam Altın'
    };
    return types[type] || type;
  };

  const getMonthName = (month) => {
    const months = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];
    return months[month - 1];
  };

  // Grafik verilerini hazırla
  const prepareChartData = () => {
    if (!stats?.monthlyUsers) {
      return null;
    }

    const labels = stats.monthlyUsers.map(item =>
      `${getMonthName(item.month)}`
    );

    const data = stats.monthlyUsers.map(item => item.count);

    return {
      labels,
      datasets: [
        {
          label: 'Yeni Üyeler',
          data: data,
          fill: true,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: 'rgb(59, 130, 246)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: {
            size: 14,
            weight: 'bold'
          },
          color: isDarkMode ? '#D1D5DB' : '#6B7280',
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.9)' : 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y} kişi`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: isDarkMode ? '#D1D5DB' : '#6B7280',
          font: {
            size: 12
          }
        },
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
        }
      },
      x: {
        ticks: {
          color: isDarkMode ? '#D1D5DB' : '#6B7280',
          font: {
            size: 12
          }
        },
        grid: {
          display: false
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-5 sm:py-8 transition-colors duration-300 ease-in-out overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 min-w-0">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              Admin Paneli
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Hoş geldin, {user?.name}! Sistemin genel durumunu buradan görebilirsin.
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="w-full sm:w-auto justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center space-x-2 transition-all sm:hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Verileri Yenile"
          >
            <FaSyncAlt className={`${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Yenileniyor...' : 'Yenile'}</span>
          </button>
        </div>

        <div className="mb-8 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 sm:p-5 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-bold text-gray-900 dark:text-white">Canonical varlık temizliği</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Yalnızca Currency current kayıtlarını raporlar; history ve portföylere dokunmaz.</p>
            </div>
            <div className="grid grid-cols-1 sm:flex w-full sm:w-auto gap-2">
              <button onClick={() => handleDuplicateCleanup(false)} disabled={cleanupLoading} className="w-full sm:w-auto px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 font-semibold disabled:opacity-50">
                Dry-run
              </button>
              <button onClick={() => handleDuplicateCleanup(true)} disabled={cleanupLoading} className="w-full sm:w-auto px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50">
                Legacy kayıtları pasifleştir
              </button>
            </div>
          </div>
          {cleanupReport && (
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
              Grup: {cleanupReport.duplicateGroupCount} · Duplicate: {cleanupReport.duplicateRecordCount} · Keep: {cleanupReport.keepCount} · Pasifleştir: {cleanupReport.deactivateCount}
            </p>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-8">
          {/* Şu An Aktif */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-100 dark:bg-green-900 p-3 rounded-xl relative">
                <FaUsers className="text-green-600 dark:text-green-400 text-2xl" />
                <FaCircle className="text-green-500 text-xs absolute -top-1 -right-1 animate-pulse" />
              </div>
              <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase">
                Çevrimiçi
              </span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {stats?.currentlyActive || 0}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Aktif Ziyaretçi
            </p>
          </div>

          {/* Toplam Kullanıcı */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-xl">
                <FaUsers className="text-blue-600 dark:text-blue-400 text-2xl" />
              </div>
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Toplam
              </span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {stats?.totalUsers || 0}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Kayıtlı Kullanıcı
            </p>
          </div>

          {/* Bugün Kayıt Olanlar */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-100 dark:bg-green-900 p-3 rounded-xl">
                <FaUserPlus className="text-green-600 dark:text-green-400 text-2xl" />
              </div>
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Bugün
              </span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {stats?.todayUsers || 0}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Yeni Üye
            </p>
          </div>

          {/* Aktif Kullanıcı (7 gün) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-xl">
                <FaChartLine className="text-purple-600 dark:text-purple-400 text-2xl" />
              </div>
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Son 7 Gün
              </span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {stats?.activeUsers || 0}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Aktif Kullanıcı
            </p>
          </div>

          {/* Toplam Varlık */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow-100 dark:bg-yellow-900 p-3 rounded-xl">
                <FaWallet className="text-yellow-600 dark:text-yellow-400 text-2xl" />
              </div>
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Toplam
              </span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {stats?.totalAssets || 0}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Kayıtlı Varlık
            </p>
          </div>
        </div>

        {/* Aylık Üye Kayıt Grafiği */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 sm:p-6 mb-8 border border-gray-200 dark:border-gray-700 overflow-hidden">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Aylık Üye Kayıtları (Son 12 Ay)
          </h2>
          <div className="h-64 sm:h-80 min-w-0 overflow-hidden">
            {stats?.monthlyUsers && prepareChartData() ? (
              <Line
                key={`chart-${stats.monthlyUsers.length}`}
                data={prepareChartData()}
                options={chartOptions}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        </div>

        {/* En Popüler Varlıklar */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8 border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            En Popüler Varlıklar
          </h2>
          <div className="space-y-4">
            {stats?.assetStats && stats.assetStats.length > 0 ? (
              stats.assetStats.map((asset, index) => (
                <div
                  key={asset._id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 w-10 h-10 rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {getAssetTypeName(asset._id)}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Toplam Miktar: {asset.totalAmount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {asset.count}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      İşlem
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-600 dark:text-gray-400 py-8">
                Henüz varlık eklenmemiş
              </p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/admin/users"
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl shadow-lg p-5 sm:p-8 hover:from-blue-600 hover:to-blue-700 transform sm:hover:scale-105 transition-all duration-300 group min-w-0"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Kullanıcı Yönetimi</h3>
                <p className="text-blue-100">
                  Tüm kullanıcıları görüntüle, düzenle ve yönet
                </p>
              </div>
              <FaUsers className="text-5xl text-white opacity-20 group-hover:opacity-30 transition-opacity" />
            </div>
          </Link>

          <Link
            to="/admin/email"
            className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl shadow-lg p-5 sm:p-8 hover:from-purple-600 hover:to-purple-700 transform sm:hover:scale-105 transition-all duration-300 group min-w-0"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Toplu Email Gönder</h3>
                <p className="text-purple-100">
                  Tüm kullanıcılara duyuru emaili gönder
                </p>
              </div>
              <FaEnvelope className="text-5xl text-white opacity-20 group-hover:opacity-30 transition-opacity" />
            </div>
          </Link>

          <Link
            to="/admin/changelog"
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl shadow-lg p-5 sm:p-8 hover:from-amber-600 hover:to-orange-600 transform sm:hover:scale-105 transition-all duration-300 group min-w-0"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Yenilikler Yönetimi</h3>
                <p className="text-amber-100">
                  Kullanıcılara gösterilecek güncellemeleri yönet
                </p>
              </div>
              <FaBullhorn className="text-5xl text-white opacity-20 group-hover:opacity-30 transition-opacity" />
            </div>
          </Link>

          <Link
            to="/admin/source-comparison"
            className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-2xl shadow-lg p-5 sm:p-8 hover:from-indigo-600 hover:to-blue-700 transform sm:hover:scale-105 transition-all duration-300 group min-w-0"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Kaynak Karşılaştırması</h3>
                <p className="text-indigo-100">TCMB ve Trunçgil günlük kur farklarını görüntüle</p>
              </div>
              <FaBalanceScale className="text-5xl text-white opacity-20 group-hover:opacity-30 transition-opacity" />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
